import { HttpInterceptorFn, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { catchError, throwError, tap } from 'rxjs';
import { SessionService } from '../services/session.service';
import { CookieService } from 'ngx-cookie-service';

/**
 * Adds `x-access-token` and `session-id` headers to every request.
 * Redirects to /login on 401 / 505 session errors.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const session = inject(SessionService);
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);
  const cookieService = inject(CookieService);

  // const token = session.token;
  // const sessionId = session.sessionId;
  //fix because of SSR where node cannot access session
  const token = cookieService.get('user.token');
  const sessionId = cookieService.get('user.session');
  let modifiedReq = req;

  // Debug: log whether token/session exist at request time and mask values
  try {
    const mask = (s?: string | null) => {
      if (!s) return null;
      if (s.length <= 8) return s;
      return `${s.slice(0, 4)}...${s.slice(-4)}`;
    };
    // eslint-disable-next-line no-console
    console.debug('[authInterceptor] outgoing request ->', req.url, {
      tokenPresent: !!token,
      tokenMasked: mask(token),
      sessionIdPresent: !!sessionId,
      sessionIdMasked: mask(sessionId)
    });
  } catch {}

  if (token || sessionId) {
    let headers = req.headers;
    if (token) {
      headers = headers.set('x-access-token', token);
    }
    if (sessionId) {
      headers = headers.set('session-id', sessionId);
    }
    modifiedReq = req.clone({ headers });
  }

  return next(modifiedReq).pipe(
    tap(event => {
      if (event instanceof HttpResponse) {
        const body: any = event.body;
        if (
          body &&
          body.success === false &&
          typeof body.message === 'string' && (
            body.message.includes('Failed to authenticate request identity') ||
            body.message.includes('Token expired') ||
            body.message.includes('session_error')
          )
        ) {
          if (isPlatformBrowser(platformId)) {
            try {
              cookieService.delete('user.token');
              cookieService.delete('user.session');
              cookieService.deleteAll();
            } catch {}
            try {
              session.clear();
            } catch {}
            router.navigate(['/login']);
          }
        }
      }
    }),
    catchError((error: HttpErrorResponse) => {
      if (
        isPlatformBrowser(platformId) &&
        (error.status === 505 || error.status === 401) &&
        error.error?.error?.type === 'session_error'
      ) {
        try {
          cookieService.delete('user.token');
          cookieService.delete('user.session');
          cookieService.deleteAll();
        } catch {}
        try {
          session.clear();
        } catch {}
        router.navigate(['/login']);
      }
      return throwError(() => error);
    })
  );
};
